/**
 * SMSWiser campaign/country creative queries
 * For Media Devoted | Created By Ciro Autiero
 */

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import api from '@/services/api';

import { keys } from '../keys';
import type { CampaignCreative } from '../types';
import { unwrapApiData } from '../utils';

/** @deprecated Use useCountryCreative - creative is now country-level */
export function useCampaignCreative(countryKey: string, _wiserId: number) {
  return useCountryCreative(countryKey);
}

interface CountryCreativeQueryOptions {
  /** When false, skip fetch (e.g. stats-only users lack creative read permission). */
  enabled?: boolean;
}

/** Country-level creative (senders, texts, title prefix). Replaces campaign-level. */
export function useCountryCreative(countryKey: string, options?: CountryCreativeQueryOptions) {
  const enabled = options?.enabled !== false;
  return useQuery({
    queryKey: keys.countryCreative(countryKey),
    queryFn: async () => {
      const res = await api.get(`/smswiser/countries/${encodeURIComponent(countryKey)}/creative`);
      return unwrapApiData<CampaignCreative & { titlePrefix?: string | null }>(res.data);
    },
    enabled: Boolean(countryKey) && enabled,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}

/** Country creative including hidden texts. Use for Texts tab to match all Voluum textIds. */
export function useCountryCreativeForStats(countryKey: string) {
  return useQuery({
    queryKey: keys.countryCreativeForStats(countryKey),
    queryFn: async () => {
      const res = await api.get(
        `/smswiser/countries/${encodeURIComponent(countryKey)}/creative?includeHiddenTexts=true`,
      );
      return unwrapApiData<CampaignCreative & { titlePrefix?: string | null }>(res.data);
    },
    enabled: Boolean(countryKey),
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
